import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import GraficosBasicos from "../../components/solicitacoes/Dashboard/GraficosBasicos";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import RenovacaoGdmService from "../../services/resource/renovacaoGdmService";
import UsuarioService from "../../services/resource/usuarioService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { kpisBasicos } from "../../utils/solicitacoes/chartOptions";

const service = new RenovacaoGdmService();
const usuarioService = new UsuarioService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));
const RENOVADO_OPTIONS = [
    { value: "SIM", label: "Renovada" },
    { value: "NAO", label: "Não renovada" },
    { value: "PENDENTE", label: "Aguardando confirmação" },
];

const FILTROS_INICIAIS_EXTRA = {
    clienteNome: "", clienteCodigo: "", numeroSerie: "", renovado: null, status: null, solicitanteId: null,
};

const renovadoLabel = v => v == null ? "-" : (v ? "Sim" : "Não");

const COLUNAS = [
    { key: "numero", label: "Nº" },
    { key: "clienteCodigo", label: "Código Cliente" },
    { key: "clienteRazaoSocial", label: "Razão Social" },
    { key: "clienteFantasia", label: "Nome Fantasia" },
    { key: "rota", label: "Rota" },
    { key: "gdmStatus", label: "GDM Status" },
    { key: "statusPdv", label: "Status PDV" },
    { key: "canal", label: "Canal" },
    { key: "cidade", label: "Cidade" },
    { key: "dataAquisicao", label: "Data Aquisição", format: "date" },
    { key: "numeroSerie", label: "Nº de Série" },
    { key: "numeroContratoCev", label: "Contrato CEV" },
    { key: "gdmCodigo", label: "GDM Código" },
    { key: "gdmDescricao", label: "GDM Descrição" },
    { key: "logomarca", label: "Logomarca" },
    { key: "justificativaAprovacao", label: "Justificativa (Aprovação)" },
    { key: "renovado", label: "Renovada", render: renovadoLabel, center: true },
    { key: "justificativaNaoRenovacao", label: "Motivo (Não Renovação)" },
    { key: "documentosNaoEnviados", label: "Documentos Não Enviados (Motivo)" },
    { key: "assinaturaColetada", label: "Assinatura", render: v => (v ? "Sim" : "Não"), center: true },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "dataSolicitacao", label: "Data da Solicitação", format: "date" },
];

export default function RelatorioRenovacaoGdm() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.renovacaoGdm.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Renovação de GDM";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    return (
        <DashboardProcessoLayout
            titulo="Renovação de GDM"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            showHeader={false}
            className="text-[11px] border shadow-card rounded-xl"
            onLimparFiltros={limparFiltros}
            kpis={kpisBasicos(dashboard, "fa fa-snowflake")}
            graficos={<GraficosBasicos dashboard={dashboard} />}
            filtrosExtras={(
                <>
                    <Campo label="Cliente">
                        <input type="text" value={filtros.clienteNome} onChange={e => setFiltro("clienteNome", e.target.value)}
                            placeholder="Razão social ou fantasia" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Código do Cliente">
                        <input type="text" value={filtros.clienteCodigo} onChange={e => setFiltro("clienteCodigo", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Nº de Série">
                        <input type="text" value={filtros.numeroSerie} onChange={e => setFiltro("numeroSerie", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Renovação">
                        <CustomDropdown value={filtros.renovado} onChange={e => setFiltro("renovado", e.value)} options={RENOVADO_OPTIONS}
                            placeholder="Todas" showClear />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportFilename="dashboard_renovacao_gdm"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
